import {
  Box,
  Grid,
  NoSsr,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState, Suspense } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomBoxFullWidth,
  CustomStackFullWidth,
} from "../../../styled-components/CustomStyles.style";
import CustomImageContainer from "../../CustomImageContainer";
import CustomContainer from "../../container";
import iconicBg from "../assets/hero_background.png";
import HeroLocationForm from "./HeroLocationForm";
import HeroTitleSection from "./HeroTitleSection";
import MobileFrame from "../assets/MobileFrame";
import { Stack } from "@mui/system";

const DynamicModuleSelection = React.lazy(() =>
  import("./module-selection/ModuleSelectionRaw")
);
const HeroSection = ({ configData, landingPageData, handleOrderNow }) => {
  const theme = useTheme();
  const isXSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const [currentLocation, setCurrentLocation] = useState(null);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentLocation(window.localStorage.getItem("location"));
    }
  }, []);

  const calculateTopMagin = () => {
    // Use consistent margins regardless of location state to prevent layout shifts
    // Mobile header height: TopNavBar (30px) + ModuleWiseNav (48px) = ~78px + extra spacing needed
    return {
      xs: "10rem", // Further increased from 8rem to ensure hero section is fully visible below header
      sm: "5rem",
      md: "7rem",
    };
  }

  return (
    <CustomContainer>
      <CustomBoxFullWidth
        sx={{
          background: (theme) => theme.palette.mode === 'dark' 
            ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          marginTop: calculateTopMagin(),
          borderRadius: "32px",
          position: "relative",
          overflow: "hidden",
          boxShadow: (theme) => theme.palette.mode === 'dark' 
            ? `0px 20px 60px rgba(0, 184, 169, 0.3)`
            : `0px 20px 60px rgba(0, 0, 0, 0.08)`,
          minHeight: { xs: "500px", md: "600px" },
          border: (theme) => theme.palette.mode === 'light' ? `1px solid ${theme.palette.divider}` : 'none',
          ".shape img": {
            transition: "all ease-in 1s",
          },
        }}
      >
        {/* Decorative overlay pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: theme.palette.mode === 'dark' ? 0.1 : 0.02,
            backgroundImage: `url(${iconicBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            zIndex: 1
          }}
        />

        {/* Gradient overlay for depth */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: (theme) => theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark || theme.palette.primary.main, 0.95)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.7)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
            zIndex: 1
          }}
        />

        <Grid container alignItems="center" sx={{ position: "relative", zIndex: 2 }}>
          <Grid item xs={12} md={7} sx={{ padding: { xs: "3rem 2rem", sm: "4rem 3rem", md: "5rem" } }}>
            <NoSsr>
              <HeroTitleSection
                configData={configData}
                landingPageData={landingPageData}
                handleOrderNow={handleOrderNow}
              />
            </NoSsr>
          </Grid>
          <Grid item xs={12} md={5} align="center" sx={{ position: "relative", padding: { xs: "2rem", md: "3rem 4rem 3rem 0" } }}>
            <CustomStackFullWidth
              height="100%"
              alignItems="center"
              justifyContent="center"
              spacing={3}
            >
              <Box
                sx={{
                  height: { xs: "280px", sm: "450px", md: "520px" },
                  width: { xs: "200px", sm: "320px", md: "360px" },
                  borderRadius: "32px",
                  position: "relative",
                  backgroundImage: `url(${landingPageData?.base_urls?.header_banner_url}/${landingPageData?.header_banner})`,
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? "0px 30px 60px rgba(0,0,0,0.5)" : "0px 30px 60px rgba(0,0,0,0.1)",
                  border: (theme) => theme.palette.mode === 'dark' 
                    ? `4px solid ${alpha(theme.palette.common.white, 0.1)}`
                    : `4px solid ${theme.palette.divider}`,
                  animation: "float 6s ease-in-out infinite",
                  "@keyframes float": {
                    "0%": { transform: "translateY(0px)" },
                    "50%": { transform: "translateY(-20px)" },
                    "100%": { transform: "translateY(0px)" },
                  },
                }}
              >
              </Box>
            </CustomStackFullWidth>
          </Grid>
        </Grid>
      </CustomBoxFullWidth>
      {isXSmall && (
        <>
          {currentLocation ? (
            <Suspense fallback={<div>Loading...</div>}>
              <DynamicModuleSelection isSmall />
            </Suspense>
          ) : (
            <CustomStackFullWidth mt="10px">
              <HeroLocationForm />
            </CustomStackFullWidth>
          )}
        </>
      )}
    </CustomContainer>
  );
};

export default HeroSection;
